import { z } from "zod";

// A patient's memberships and packages, as `/patient-app/plans/get` answers
// (the server's patient-safe projection, memberships audit C48). Tolerant
// parsing: a field the server adds later never breaks the tab.

const CivilDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional();

export const PlanItemSchema = z.object({
  name: z.string(),
  kind: z.enum(["service", "product"]).catch("service"),
  unlimited: z.boolean().optional().default(false),
  discountPercent: z.number().optional(),
  discountCents: z.number().optional(),
  total: z.number().nullable().optional(),
  remaining: z.number().nullable().optional(),
  used: z.number().optional().default(0),
  expiresOn: CivilDate,
  expiredUnused: z.number().optional(),
  opensOn: CivilDate,
});
export type PlanItem = z.infer<typeof PlanItemSchema>;

export const PatientPlanSchema = z.object({
  planId: z.string(),
  name: z.string(),
  kind: z.enum(["membership", "package"]).catch("package"),
  state: z.string(),
  stateOn: CivilDate,
  validity: z
    .object({
      kind: z.string(),
      on: CivilDate,
      durationMonths: z.number().optional(),
    })
    .catch({ kind: "none" }),
  usableAt: z
    .object({
      everywhere: z.boolean().optional(),
      locations: z.array(z.string()).optional(),
    })
    .catch({}),
  note: z.string().nullable().optional(),
  items: z.array(PlanItemSchema).catch([]),
  renewal: z
    .object({
      state: z.string().nullable().optional(),
      renewsOn: CivilDate,
      endsOn: CivilDate,
      interval: z.string().nullable().optional(),
      nextChargeCents: z.number().nullable().optional(),
      currency: z.string().optional().default("CAD"),
    })
    .nullable()
    .optional(),
});
export type PatientPlan = z.infer<typeof PatientPlanSchema>;

export const PatientPlansResponseSchema = z.object({
  success: z.boolean(),
  plans: z.array(PatientPlanSchema),
  offered: z.boolean().optional().default(false),
});
export type PatientPlansResponse = z.infer<typeof PatientPlansResponseSchema>;
